import { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useCompliance } from "@/hooks/useOrgLibrary";
import { orgLibraryService } from "@/services/orgLibraryService";
import { ShieldCheck, ChevronDown, ChevronRight, CheckCircle2, Clock, Download } from "lucide-react";

const VIS_LABEL = { org: "Whole org", teams: "Teams", members: "Members" };

export function CompliancePanel() {
  const { data: courses = [] } = useCompliance();
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const downloadCert = async (courseId, m) => {
    setBusy(`${courseId}-${m.id}`);
    try {
      await orgLibraryService.memberCertificate(courseId, m.id, m.name);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mb-12" data-testid="compliance-panel">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h2 className="font-display text-2xl font-bold tracking-tight">Compliance</h2>
      </div>
      {courses.length === 0 ? (
        <div className="border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="compliance-empty">
          No mandatory courses yet. Mark a shared course as <span className="font-semibold text-foreground">Mandatory</span> to start tracking completion.
        </div>
      ) : (
        <div className="space-y-3" data-testid="compliance-list">
          {courses.map((c) => {
            const pct = c.eligible_count ? Math.round((c.completed_count / c.eligible_count) * 100) : 0;
            const isOpen = open[c.id];
            return (
              <div key={c.id} className="border border-border bg-card" data-testid={`compliance-course-${c.id}`}>
                <button onClick={() => toggle(c.id)} className="w-full flex items-center gap-4 px-5 py-4 text-left" data-testid={`compliance-toggle-${c.id}`}>
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground">{c.length_label} · shared with {VIS_LABEL[c.visibility] || c.visibility}</div>
                  </div>
                  <div className="w-40 shrink-0">
                    <div className="flex justify-between text-xs mb-1"><span className="font-semibold" data-testid={`compliance-count-${c.id}`}>{c.completed_count}/{c.eligible_count}</span><span className="text-muted-foreground">{pct}%</span></div>
                    <div className="h-1.5 bg-secondary"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-border px-5 py-4 grid sm:grid-cols-2 gap-6" data-testid={`compliance-detail-${c.id}`}>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-primary font-bold mb-2 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Completed ({c.completions.length})</p>
                      {c.completions.length === 0 ? <p className="text-sm text-muted-foreground">Nobody yet.</p> :
                        <ul className="space-y-1">{c.completions.map((m) => (
                          <li key={m.id} className="text-sm flex items-center justify-between gap-2">
                            <span className="truncate flex-1">{m.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">{new Date(m.completed_at).toLocaleDateString()}</span>
                            <button onClick={() => downloadCert(c.id, m)} disabled={busy === `${c.id}-${m.id}`}
                              title="Download certificate" className="text-muted-foreground hover:text-primary shrink-0" data-testid={`download-cert-${c.id}-${m.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}</ul>}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Pending ({c.pending.length})</p>
                      {c.pending.length === 0 ? <p className="text-sm text-muted-foreground">Everyone's done! 🎉</p> :
                        <ul className="space-y-1">{c.pending.map((m) => (
                          <li key={m.id} className="text-sm truncate">{m.name}</li>
                        ))}</ul>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
